import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, History, Settings, User, Phone, Mail, Key, ChevronRight,
  ArrowLeftRight, Shield, Star, MessageCircle, Clock, CheckCircle,
  Filter, Search, AlertCircle, ThumbsUp, BadgeCheck, Users, Wallet,
  TrendingUp, Globe, Bell, CreditCard, Building, Smartphone, RefreshCw,
  Monitor
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

// Default fallback rates
const DEFAULT_RATES = {
  BTC: 8500000,
  ETH: 320000,
  USDT: 92,
  USD: 92,
  RUB: 1
};

const CryptoIcon = ({ symbol, size = "w-8 h-8" }) => {
  const icons = {
    BTC: { bg: 'linear-gradient(135deg, #F7931A 0%, #FF9500 100%)', text: '₿' },
    ETH: { bg: 'linear-gradient(135deg, #627EEA 0%, #8B9FE8 100%)', text: 'Ξ' },
    USDT: { bg: 'linear-gradient(135deg, #26A17B 0%, #3CC68A 100%)', text: '$' },
    RUB: { bg: 'linear-gradient(135deg, #4A5568 0%, #718096 100%)', text: '₽' },
  };
  const icon = icons[symbol] || { bg: '#666', text: '?' };
  return <div className={`${size} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg`} style={{ background: icon.bg }}>{icon.text}</div>;
};

const formatNumber = (num, decimals = 2) => parseFloat(num || 0).toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export default function BitExchangeCombinedPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('light');
  const [currentPage, setCurrentPage] = useState('main');
  const [historyFilter, setHistoryFilter] = useState('all');
  const [tradeType, setTradeType] = useState('buy');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exchangeRates, setExchangeRates] = useState(DEFAULT_RATES);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState(null);
  const [showFrame, setShowFrame] = useState(true); // NEW: Frame toggle

  // Function to convert amount to RUB using current rates
  const toRub = useCallback((amount, currency) => {
    if (!amount || !currency) return 0;
    const rate = exchangeRates[currency] || exchangeRates[currency.toUpperCase()] || 1;
    return parseFloat(amount) * rate;
  }, [exchangeRates]);

  // Fetch real exchange rates from CoinGecko API
  const fetchExchangeRates = useCallback(async () => {
    setRatesLoading(true);
    setRatesError(null);
    try {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,tether&vs_currencies=rub'
      );
      if (!response.ok) throw new Error('Failed to fetch rates');
      const data = await response.json();
      
      const newRates = {
        BTC: data.bitcoin?.rub || DEFAULT_RATES.BTC,
        ETH: data.ethereum?.rub || DEFAULT_RATES.ETH,
        USDT: data.tether?.rub || DEFAULT_RATES.USDT,
        USD: data.tether?.rub || DEFAULT_RATES.USD, // Use USDT as USD proxy
        RUB: 1
      };
      
      setExchangeRates(newRates);
      console.log('Fetched exchange rates:', newRates);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      setRatesError('Не удалось загрузить курсы');
      // Keep default rates
    } finally {
      setRatesLoading(false);
    }
  }, []);

  // Fetch rates on mount
  useEffect(() => {
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    const pageParam = searchParams.get('page');
    const frameParam = searchParams.get('frame'); // NEW: Frame parameter
    if (themeParam) setBrowserTheme(themeParam);
    if (pageParam && ['main', 'history', 'settings'].includes(pageParam)) setCurrentPage(pageParam);
    if (frameParam !== null) setShowFrame(frameParam !== 'false' && frameParam !== '0'); // NEW
    if (dataParam) { 
      try { 
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setData(parsedData);
        
        // Auto-calculate date range from transactions
        if (parsedData.transactions && parsedData.transactions.length > 0) {
          const dates = parsedData.transactions
            .filter(tx => tx.date)
            .map(tx => {
              const parts = tx.date.split('.');
              if (parts.length === 3) {
                return { 
                  original: tx.date, 
                  sortValue: parseInt(parts[2]) * 10000 + parseInt(parts[1]) * 100 + parseInt(parts[0]) 
                };
              }
              return null;
            })
            .filter(d => d !== null);
          
          if (dates.length > 0) {
            dates.sort((a, b) => a.sortValue - b.sortValue);
            setDateFrom(dates[0].original);
            setDateTo(dates[dates.length - 1].original);
          }
        }
      } catch (e) { console.error(e); } 
    }
  }, [searchParams]);

  useEffect(() => {
    const titles = { main: 'P2P Маркет', history: 'Мои сделки', settings: 'Профиль' };
    document.title = `${titles[currentPage]} | BitExchange`;
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) { favicon = document.createElement('link'); favicon.rel = 'icon'; document.head.appendChild(favicon); }
    favicon.href = '/bitexchange-favicon.svg';
  }, [currentPage]);

  if (!data) return <div className="min-h-screen bg-purple-900 flex items-center justify-center text-white">Загрузка...</div>;

  const isDark = browserTheme === 'dark';
  const theme = { 
    bg: isDark ? '#1a1625' : '#f8f7fc', 
    card: isDark ? '#252033' : '#ffffff',
    activeTabBg: isDark ? '#252033' : '#ffffff', 
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4', 
    textPrimary: isDark ? '#e8eaed' : '#1a1625', 
    textSecondary: isDark ? '#9aa0a6' : '#6b7280',
    border: isDark ? '#3d3650' : '#e5e7eb',
    accent: '#8b5cf6'
  };
  
  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '000000';
  const rating = data.rating || '4.9';
  const dealsCount = data.dealsCount || 127;

  const cryptoBalances = [
    { symbol: 'USDT', balance: data.usdtBalance || '0.00' },
    { symbol: 'BTC', balance: data.btcBalance || '0.000000' },
    { symbol: 'ETH', balance: data.ethBalance || '0.0000' },
    { symbol: 'RUB', balance: data.balance || '0' },
  ];

  const totalRub = cryptoBalances.reduce((sum, c) => c.symbol === 'RUB' ? sum + parseFloat(c.balance || 0) : sum + toRub(c.balance, c.symbol), 0);

  // P2P Offers
  const buyOffers = [
    { trader: 'CryptoKing', rating: '4.98', deals: 1523, price: '76.85', min: '10,000', max: '500,000', methods: ['Сбербанк', 'Тинькофф'], online: true },
    { trader: 'FastTrade', rating: '4.92', deals: 892, price: '76.90', min: '5,000', max: '300,000', methods: ['СБП', 'Альфа'], online: true },
    { trader: 'TrustDealer', rating: '4.87', deals: 456, price: '77.00', min: '1,000', max: '100,000', methods: ['Тинькофф'], online: false },
    { trader: 'SafeExchange', rating: '4.95', deals: 2341, price: '77.10', min: '20,000', max: '1,000,000', methods: ['Сбербанк', 'ВТБ', 'СБП'], online: true },
  ];

  const sellOffers = [
    { trader: 'QuickSell', rating: '4.91', deals: 678, price: '75.90', min: '5,000', max: '200,000', methods: ['Сбербанк'], online: true },
    { trader: 'TopTrader', rating: '4.96', deals: 1876, price: '75.80', min: '10,000', max: '500,000', methods: ['Тинькофф', 'СБП'], online: true },
    { trader: 'ProDealer', rating: '4.89', deals: 543, price: '75.70', min: '1,000', max: '150,000', methods: ['Альфа', 'Райффайзен'], online: false },
  ];

  const paymentMethods = [
    { id: 'sber', name: 'Сбербанк', icon: Building },
    { id: 'tinkoff', name: 'Тинькофф', icon: CreditCard },
    { id: 'sbp', name: 'СБП', icon: Smartphone },
    { id: 'alfa', name: 'Альфа-Банк', icon: Building },
  ];

  const menuItems = [
    { id: 'main', icon: ArrowLeftRight, label: 'P2P Маркет' },
    { id: 'myads', icon: Users, label: 'Мои объявления' },
    { id: 'wallet', icon: Wallet, label: 'Кошелёк' },
    { id: 'history', icon: History, label: 'История сделок' },
    { id: 'messages', icon: MessageCircle, label: 'Сообщения' },
    { id: 'settings', icon: Settings, label: 'Настройки' },
  ];

  const BrowserFrame = () => (
    <div style={{ backgroundColor: isDark ? '#1a1625' : '#dee1e6' }} className="flex-shrink-0">
      <div className="flex items-center h-[38px] pl-2">
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] mr-[1px]" style={{ backgroundColor: theme.inactiveTabBg }}><img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2" /><span className="text-[13px]" style={{ color: theme.textSecondary }}>Ozon Карта</span></div>
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px]" style={{ backgroundColor: theme.activeTabBg }}><img src="/bitexchange-favicon.svg" alt="" className="w-4 h-4 mr-2" /><span className="text-[13px]" style={{ color: theme.textPrimary }}>BitExchange P2P</span></div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4" style={{ backgroundColor: theme.activeTabBg }}>
        <div className="flex gap-2">{['←', '→', '↻'].map((a, i) => <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ color: theme.textSecondary }}>{a}</button>)}</div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center" style={{ backgroundColor: isDark ? '#1a1625' : '#f1f3f4' }}><span className="text-[14px]" style={{ color: theme.textSecondary }}>🔒 bitexchange.io/p2p/usdt-rub</span></div>
      </div>
    </div>
  );

  const MainPage = () => (
    <div className="p-6 space-y-6" style={{ backgroundColor: theme.bg }}>
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
          <div className="text-sm" style={{ color: theme.textSecondary }}>24ч Объём</div>
          <div className="text-xl font-bold" style={{ color: theme.textPrimary }}>₽ 45.2M</div>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
          <div className="text-sm" style={{ color: theme.textSecondary }}>Сделок сегодня</div>
          <div className="text-xl font-bold" style={{ color: theme.textPrimary }}>1,234</div>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
          <div className="text-sm" style={{ color: theme.textSecondary }}>Активных трейдеров</div>
          <div className="text-xl font-bold" style={{ color: theme.textPrimary }}>456</div>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
          <div className="text-sm" style={{ color: theme.textSecondary }}>Средний курс</div>
          <div className="text-xl font-bold" style={{ color: theme.textPrimary }}>₽ 76.50</div>
        </div>
      </div>

      {/* Trade Type Tabs */}
      <div className="flex items-center gap-4">
        <div className="flex rounded-xl overflow-hidden" style={{ backgroundColor: theme.card }}>
          <button
            onClick={() => setTradeType('buy')}
            className={`px-6 py-3 font-medium transition-colors ${tradeType === 'buy' ? 'bg-green-500 text-white' : ''}`}
            style={tradeType !== 'buy' ? { color: theme.textPrimary } : {}}
          >
            Купить USDT
          </button>
          <button
            onClick={() => setTradeType('sell')}
            className={`px-6 py-3 font-medium transition-colors ${tradeType === 'sell' ? 'bg-red-500 text-white' : ''}`}
            style={tradeType !== 'sell' ? { color: theme.textPrimary } : {}}
          >
            Продать USDT
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ backgroundColor: theme.card }}>
            <Search className="w-4 h-4" style={{ color: theme.textSecondary }} />
            <input placeholder="Сумма в RUB" className="bg-transparent outline-none w-32" style={{ color: theme.textPrimary }} />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ backgroundColor: theme.card, color: theme.textPrimary }}>
            <Filter className="w-4 h-4" />
            Фильтры
          </button>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="flex items-center gap-2">
        <span className="text-sm" style={{ color: theme.textSecondary }}>Способ оплаты:</span>
        {paymentMethods.map(m => (
          <button key={m.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm" style={{ backgroundColor: theme.card, color: theme.textPrimary }}>
            <m.icon className="w-4 h-4" />
            {m.name}
          </button>
        ))}
      </div>

      {/* Offers List */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: theme.card }}>
        <div className="grid grid-cols-6 gap-4 px-4 py-3 text-sm font-medium border-b" style={{ color: theme.textSecondary, borderColor: theme.border }}>
          <div>Трейдер</div>
          <div>Цена</div>
          <div>Лимиты</div>
          <div>Способы оплаты</div>
          <div>Статус</div>
          <div></div>
        </div>
        {(tradeType === 'buy' ? buyOffers : sellOffers).map((offer, i) => (
          <div key={i} className="grid grid-cols-6 gap-4 px-4 py-4 items-center border-b last:border-0 hover:bg-purple-500/5 transition-colors" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center font-bold text-purple-400">
                {offer.trader.charAt(0)}
              </div>
              <div>
                <div className="flex items-center gap-1" style={{ color: theme.textPrimary }}>
                  {offer.trader}
                  {parseFloat(offer.rating) >= 4.9 && <BadgeCheck className="w-4 h-4 text-purple-400" />}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Star className="w-3 h-3 text-yellow-400" />
                  <span style={{ color: theme.textSecondary }}>{offer.rating}</span>
                  <span style={{ color: theme.textSecondary }}>• {offer.deals} сделок</span>
                </div>
              </div>
            </div>
            <div>
              <div className="font-bold text-lg" style={{ color: tradeType === 'buy' ? '#22c55e' : '#ef4444' }}>
                {offer.price} ₽
              </div>
              <div className="text-xs" style={{ color: theme.textSecondary }}>за 1 USDT</div>
            </div>
            <div style={{ color: theme.textSecondary }}>
              <div>{offer.min} - {offer.max} ₽</div>
            </div>
            <div className="flex flex-wrap gap-1">
              {offer.methods.map((m, j) => (
                <span key={j} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: isDark ? '#3d3650' : '#f3f4f6', color: theme.textPrimary }}>
                  {m}
                </span>
              ))}
            </div>
            <div>
              <span className={`flex items-center gap-1 text-sm ${offer.online ? 'text-green-400' : 'text-gray-400'}`}>
                <span className={`w-2 h-2 rounded-full ${offer.online ? 'bg-green-400' : 'bg-gray-400'}`} />
                {offer.online ? 'Онлайн' : 'Офлайн'}
              </span>
            </div>
            <div>
              <button className={`px-4 py-2 rounded-lg font-medium text-white ${tradeType === 'buy' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} transition-colors`}>
                {tradeType === 'buy' ? 'Купить' : 'Продать'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Info Banner */}
      <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
        <Shield className="w-8 h-8 text-purple-400" />
        <div>
          <div className="font-medium" style={{ color: theme.textPrimary }}>Безопасные сделки с эскроу</div>
          <div className="text-sm" style={{ color: theme.textSecondary }}>Все сделки защищены смарт-контрактом. Средства замораживаются до подтверждения обеими сторонами.</div>
        </div>
      </div>
    </div>
  );

  const HistoryPage = () => {
    let transactions = data.transactions || [];
    if (transactions.length === 0) {
      transactions = [
        { type: 'deposit', amount: '500', currency: 'USDT', date: '03.02.2026', time: '14:32', status: 'completed', counterparty: 'CryptoKing', price: '76.85' },
        { type: 'withdrawal', amount: '300', currency: 'USDT', date: '02.02.2026', time: '10:15', status: 'completed', counterparty: 'FastTrade', price: '76.20' },
        { type: 'deposit', amount: '1000', currency: 'USDT', date: '01.02.2026', time: '18:45', status: 'completed', counterparty: 'TrustDealer', price: '76.50' },
      ];
    }

    // Parse date helper (DD.MM.YYYY)
    const parseDate = (dateStr) => {
      if (!dateStr) return 0;
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        return parseInt(parts[2]) * 10000 + parseInt(parts[1]) * 100 + parseInt(parts[0]);
      }
      return 0;
    };

    // Sort transactions by date (oldest first - ascending)
    let sorted = [...transactions].sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      if (dateA !== dateB) return dateA - dateB;
      // If same date, sort by time
      const timeA = a.time ? a.time.replace(':', '') : '0000';
      const timeB = b.time ? b.time.replace(':', '') : '0000';
      return parseInt(timeA) - parseInt(timeB);
    });

    // Filter by date range FIRST (before calculating stats)
    let dateFiltered = sorted;
    if (dateFrom || dateTo) {
      const fromValue = dateFrom ? parseDate(dateFrom) : 0;
      const toValue = dateTo ? parseDate(dateTo) : 99999999;
      dateFiltered = sorted.filter(tx => {
        const txDate = parseDate(tx.date);
        return txDate >= fromValue && txDate <= toValue;
      });
    }

    // Calculate stats from filtered transactions (by date only, not by type)
    // deposit = продажа (sell), withdrawal = покупка (buy)
    // ВАЖНО: amount - это сумма сделки в РУБЛЯХ, не количество криптовалюты!
    const totalBoughtRub = dateFiltered
      .filter(tx => tx.type === 'withdrawal')
      .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

    const totalSoldRub = dateFiltered
      .filter(tx => tx.type === 'deposit')
      .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

    const totalTurnover = totalBoughtRub + totalSoldRub;

    // Filter by type (for display only)
    let filtered = dateFiltered;
    if (historyFilter === 'buy') filtered = filtered.filter(tx => tx.type === 'withdrawal');
    else if (historyFilter === 'sell') filtered = filtered.filter(tx => tx.type === 'deposit');

    return (
      <div className="p-6 space-y-4" style={{ backgroundColor: theme.bg }}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold" style={{ color: theme.textPrimary }}>История сделок</h2>
          <div className="flex items-center gap-2 text-sm" style={{ color: theme.textSecondary }}>
            <CheckCircle className="w-4 h-4 text-green-400" />
            {dealsCount} успешных сделок
          </div>
        </div>

        {/* Date Filter */}
        <div className="flex items-center gap-3 p-4 rounded-xl" style={{ backgroundColor: theme.card }}>
          <Filter className="w-5 h-5" style={{ color: theme.textSecondary }} />
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{ color: theme.textSecondary }}>с</span>
            <input
              type="text"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="ДД.ММ.ГГГГ"
              className="px-3 py-1.5 rounded-lg text-sm w-28 border"
              style={{ 
                backgroundColor: theme.bg, 
                color: theme.textPrimary, 
                borderColor: theme.border 
              }}
            />
            <span className="text-sm" style={{ color: theme.textSecondary }}>по</span>
            <input
              type="text"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="ДД.ММ.ГГГГ"
              className="px-3 py-1.5 rounded-lg text-sm w-28 border"
              style={{ 
                backgroundColor: theme.bg, 
                color: theme.textPrimary, 
                borderColor: theme.border 
              }}
            />
          </div>
          <div className="ml-auto text-xs" style={{ color: theme.textSecondary }}>
            Сделок в периоде: {dateFiltered.length}
          </div>
        </div>

        {/* Stats - calculated from filtered transactions */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
            <div className="text-sm" style={{ color: theme.textSecondary }}>Всего куплено</div>
            <div className="text-lg font-bold text-green-400">{formatNumber(totalBoughtRub, 0)} ₽</div>
            <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
              {dateFiltered.filter(tx => tx.type === 'withdrawal').length} сделок
            </div>
          </div>
          <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
            <div className="text-sm" style={{ color: theme.textSecondary }}>Всего продано</div>
            <div className="text-lg font-bold text-red-400">{formatNumber(totalSoldRub, 0)} ₽</div>
            <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
              {dateFiltered.filter(tx => tx.type === 'deposit').length} сделок
            </div>
          </div>
          <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
            <div className="text-sm" style={{ color: theme.textSecondary }}>Оборот</div>
            <div className="text-lg font-bold" style={{ color: theme.textPrimary }}>{formatNumber(totalTurnover, 0)} ₽</div>
            <div className="text-xs mt-1" style={{ color: theme.textSecondary }}>
              за выбранный период
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {[{ id: 'all', label: 'Все' }, { id: 'buy', label: 'Покупки' }, { id: 'sell', label: 'Продажи' }].map(f => (
            <button
              key={f.id}
              onClick={() => setHistoryFilter(f.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${historyFilter === f.id ? 'bg-purple-500 text-white' : ''}`}
              style={historyFilter !== f.id ? { backgroundColor: theme.card, color: theme.textPrimary } : {}}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Transactions */}
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: theme.card }}>
          {filtered.map((tx, i) => {
            const currency = tx.currency || 'RUB';
            const amount = parseFloat(tx.amount || 0);
            return (
              <div key={i} className="flex items-center justify-between p-4 border-b last:border-0" style={{ borderColor: theme.border }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'withdrawal' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                    {tx.type === 'withdrawal' ? <ArrowLeftRight className="w-5 h-5 text-green-400 rotate-180" /> : <ArrowLeftRight className="w-5 h-5 text-red-400" />}
                  </div>
                  <div>
                    <div style={{ color: theme.textPrimary }}>
                      {tx.type === 'withdrawal' ? 'Покупка' : 'Продажа'} {currency}
                    </div>
                    <div className="flex items-center gap-2 text-sm" style={{ color: theme.textSecondary }}>
                      <span>с {tx.counterparty}</span>
                      <span>•</span>
                      <span>{tx.date}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${tx.type === 'withdrawal' ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.type === 'withdrawal' ? '+' : '-'}{formatNumber(amount, 0)} ₽
                  </div>
                  <div className="text-sm" style={{ color: theme.textSecondary }}>
                    {tx.time}
                  </div>
                </div>
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const SettingsPage = () => (
    <div className="p-6 space-y-4" style={{ backgroundColor: theme.bg }}>
      <h2 className="text-xl font-bold" style={{ color: theme.textPrimary }}>Профиль трейдера</h2>
      
      {/* Profile Card */}
      <div className="rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-3xl font-bold text-white">
            {accountName.charAt(0)}
          </div>
          <div className="text-white">
            <div className="flex items-center gap-2">
              <span className="font-bold text-xl">{accountName}</span>
              {data.verified && <BadgeCheck className="w-5 h-5" />}
            </div>
            <div className="text-sm opacity-80">ID: {userId}</div>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="font-medium">{rating}</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                <span>{dealsCount} сделок</span>
              </div>
              <div className="flex items-center gap-1">
                <ThumbsUp className="w-4 h-4" />
                <span>98% положительных</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Balances */}
      <div className="rounded-xl p-4" style={{ backgroundColor: theme.card }}>
        <div className="font-medium mb-3" style={{ color: theme.textPrimary }}>Баланс кошелька</div>
        <div className="space-y-3">
          {cryptoBalances.map((c, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CryptoIcon symbol={c.symbol} size="w-8 h-8" />
                <span style={{ color: theme.textPrimary }}>{c.symbol}</span>
              </div>
              <div className="text-right">
                <div style={{ color: theme.textPrimary }}>{c.balance}</div>
                <div className="text-sm" style={{ color: theme.textSecondary }}>
                  ≈ {formatNumber(c.symbol === 'RUB' ? c.balance : toRub(c.balance, c.symbol), 0)} ₽
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t flex justify-between items-center" style={{ borderColor: theme.border }}>
          <span className="font-medium" style={{ color: theme.textPrimary }}>Общий баланс</span>
          <span className="text-xl font-bold" style={{ color: theme.accent }}>{formatNumber(totalRub, 0)} ₽</span>
        </div>
      </div>

      {/* Settings */}
      <div className="rounded-xl divide-y" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        {[
          { icon: User, label: 'Имя', value: accountName },
          { icon: Mail, label: 'Email', value: data.email },
          { icon: Phone, label: 'Телефон', value: data.phone },
          { icon: Shield, label: 'Верификация', value: 'Пройдена', highlight: true },
          { icon: Bell, label: 'Уведомления', value: 'Включены' },
        ].map((item, i) => (
          <div key={i} className="p-4 flex items-center justify-between" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5" style={{ color: item.highlight ? '#22c55e' : theme.textSecondary }} />
              <span style={{ color: theme.textSecondary }}>{item.label}</span>
            </div>
            <span style={{ color: item.highlight ? '#22c55e' : theme.textPrimary }}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen template-preview-scale" style={{ backgroundColor: theme.bg }}>
      {showFrame && <BrowserFrame />}
      <div className="flex" style={{ minHeight: showFrame ? 'calc(100vh - 78px)' : '100vh' }}>
        {/* Sidebar */}
        <div className="w-64 flex flex-col" style={{ backgroundColor: theme.card, borderRight: `1px solid ${theme.border}` }}>
          <div className="p-4 border-b" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg overflow-hidden" 
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}>
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <div>
                <div className="font-bold" style={{ color: theme.textPrimary }}>BitExchange</div>
                <div className="text-xs" style={{ color: theme.textSecondary }}>P2P Platform</div>
              </div>
            </div>
          </div>
          
          {/* Frame Toggle */}
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4" style={{ color: theme.textSecondary }} />
              <span className="text-sm" style={{ color: theme.textSecondary }}>Рамка браузера</span>
            </div>
            <Switch 
              checked={showFrame} 
              onCheckedChange={setShowFrame}
            />
          </div>
          
          <div className="flex-1 py-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(['myads', 'wallet', 'messages'].includes(item.id) ? 'main' : item.id)}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                  currentPage === item.id ? 'border-r-2' : ''
                }`}
                style={{
                  backgroundColor: currentPage === item.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                  color: currentPage === item.id ? theme.accent : theme.textSecondary,
                  borderColor: theme.accent
                }}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          
          <div className="p-4 border-t" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', color: theme.accent }}>
                {accountName.charAt(0)}
              </div>
              <div>
                <div className="font-medium" style={{ color: theme.textPrimary }}>{accountName}</div>
                <div className="flex items-center gap-1 text-xs">
                  <Star className="w-3 h-3 text-yellow-400" />
                  <span style={{ color: theme.textSecondary }}>{rating} • {dealsCount} сделок</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {currentPage === 'main' && <MainPage />}
          {currentPage === 'history' && <HistoryPage />}
          {currentPage === 'settings' && <SettingsPage />}
        </div>
      </div>
    </div>
  );
}
